import { fileStore } from '@/features/chats/model/draft/fileStore';
import {
   addMediaFiles,
   clearMediaPreview,
   removeMediaFile,
   setActiveFileId,
   setMediaFiles,
   updateFileMetadata,
   setIsProcessingMedia,
} from '@/features/chats/model/draft/reducer';
import { clearPostPreview } from '@/features/chats/model/mediaPreview/reducer';
import { extractFileMetadata, processFilesQuick } from '@/features/chats/sub-features/media/utils/fileProcessor';
import { useChatFeatures } from '@/features/chats/chatsRegistry';
import { validateFile } from '@/features/chats/utils/FileUploadValidation';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeSidePanel } from '@/app/store';

const DEFAULT_DRAFT = {
   message: '',
   fileIds: [],
   activeFileId: null,
   mediaType: null,
   fileMetadata: {},
   isProcessingMedia: false,
};

const toArray = files => (Array.isArray(files) ? files : [files]);

/**
 * Hook for file-level operations (used in Media components).
 * Uses a 2-phase pipeline for instant UI + background SDK enrichment.
 *
 * @param {string} activeChatId - Active chat ID
 * @param {string} fileId       - File ID to work with
 */
export const useMediaFile = ({ activeChatId, fileId }) => {
   const dispatch = useDispatch();
   const drafts = useSelector(state => state.draftData.drafts);
   const { attachments: mediaFeatures } = useChatFeatures(['attachments']);

   const currentDraft = useMemo(() => drafts[activeChatId] ?? DEFAULT_DRAFT, [drafts, activeChatId]);
   const {
      fileIds: draftFileIds,
      fileMetadata: draftFileMetadata,
      mediaType: draftMediaType,
      isProcessingMedia,
   } = currentDraft;

   const file = useMemo(() => (fileId ? fileStore.getFile(fileId) : null), [fileId]);

   const metadata = useMemo(() => (fileId ? (draftFileMetadata?.[fileId] ?? {}) : {}), [fileId, draftFileMetadata]);

   const caption = useMemo(
      () => ({
         caption: metadata.caption ?? '',
         mentionUserIds: metadata.mentionUserIds ?? [],
      }),
      [metadata.caption, metadata.mentionUserIds],
   );

   const isMediaPreview = useMemo(
      () => !!(draftFileIds?.length && (currentDraft.showPreview ?? true)),
      [draftFileIds, currentDraft.showPreview],
   );

   const dispatchIfChat = useCallback(
      action => {
         if (activeChatId) dispatch(action);
      },
      [dispatch, activeChatId],
   );

   // ─── Phase 2: background SDK enrichment (non-blocking) ─────────────────────
   //
   // Called AFTER Redux is already updated with base metadata (Phase 1).
   // Fetches SDK data (dimensions, thumbnails) sequentially per file and patches
   // Redux individually so each file updates as soon as its SDK call resolves —
   // large files don't block small ones.

   const enrichMediaMetadata = useCallback(
      async (validatedFiles, fileIds, fileMetadata) => {
         dispatch(setIsProcessingMedia({ jid: activeChatId, isProcessing: true }));
         for (let i = 0; i < fileIds.length; i++) {
            const id = fileIds[i];
            const { msgType } = fileMetadata[id];
            if (msgType !== 'image' && msgType !== 'video' && msgType !== 'audio') continue;
            try {
               const patch = await extractFileMetadata(validatedFiles[i], msgType);
               dispatch(updateFileMetadata({ jid: activeChatId, fileId: id, metadata: patch }));
            } catch (error) {
               console.log('error', error);
            }
         }
         dispatch(setIsProcessingMedia({ jid: activeChatId, isProcessing: false }));
      },
      [dispatch, activeChatId],
   );

   // ─── File operations ────────────────────────────────────────────────────────

   const saveCaption = useCallback(
      (captionText, mentionUserIds = []) => {
         if (activeChatId && fileId) {
            dispatch(
               updateFileMetadata({ jid: activeChatId, fileId, metadata: { caption: captionText, mentionUserIds } }),
            );
         }
      },
      [dispatch, activeChatId, fileId],
   );

   /**
    * Phase 1: validate → processFilesQuick (sync) → dispatch immediately
    * Phase 2: enrichMediaMetadata fires in background, patching each file as SDK resolves
    */
   const openMediaPreview = useCallback(
      async (files, mediaType, showPreview = true, options = {}) => {
         if (!activeChatId) return;
         dispatch(closeSidePanel());
         const validated = await validateFile(toArray(files), mediaType);
         if (!validated.length) return;

         // Phase 1 — instant dispatch
         const { fileIds, fileMetadata } = processFilesQuick(validated, options);
         dispatch(
            setMediaFiles({ jid: activeChatId, fileIds, fileMetadata, mediaType: mediaType ?? 'file', showPreview }),
         );

         // Phase 2 — background enrichment (e.g. reading audio duration). Returned so a
         // caller that needs the real metadata before sending (audio recorder's
         // send-after-record flow) can await it — the metadata read itself is now bounded
         // by a timeout (see getAudioDuration), so this can no longer hang or lose the
         // send the way it used to; it can only ever add a few hundred ms, worst case the
         // timeout, before resolving.
         return enrichMediaMetadata(validated, fileIds, fileMetadata);
      },
      [dispatch, activeChatId, enrichMediaMetadata],
   );

   const addMoreFiles = useCallback(
      async files => {
         if (!activeChatId) return;
         const validated = await validateFile(toArray(files), draftMediaType, draftFileIds?.length ?? 0);
         if (!validated.length) return;

         // Phase 1 — instant dispatch
         const { fileIds, fileMetadata } = processFilesQuick(validated);
         dispatch(addMediaFiles({ jid: activeChatId, fileIds, fileMetadata }));

         // Phase 2 — background
         enrichMediaMetadata(validated, fileIds, fileMetadata);
      },
      [dispatch, activeChatId, draftFileIds, draftMediaType, enrichMediaMetadata],
   );

   const removeFile = useCallback(
      (targetFileId = fileId) => {
         if (!activeChatId || !targetFileId) return;
         const blobUrl = draftFileMetadata?.[targetFileId]?.blobUrl;
         if (blobUrl) URL.revokeObjectURL(blobUrl);
         fileStore.removeFile(targetFileId);
         dispatch(removeMediaFile({ jid: activeChatId, fileId: targetFileId }));
      },
      [dispatch, activeChatId, fileId, draftFileMetadata],
   );

   const closeMediaPreview = useCallback(
      () => dispatchIfChat(clearMediaPreview(activeChatId)),
      [dispatchIfChat, activeChatId],
   );

   const closePostPreview = useCallback(() => dispatchIfChat(clearPostPreview()), [dispatchIfChat]);

   const setActiveFile = useCallback(
      newFileId => dispatchIfChat(setActiveFileId({ jid: activeChatId, fileId: newFileId })),
      [dispatchIfChat, activeChatId],
   );

   const getFileById = useCallback((targetFileId = fileId) => fileStore.getFile(targetFileId), [fileId]);

   const getFilesWithMetadata = useCallback(
      () =>
         (draftFileIds ?? [])
            .map(id => ({ fileId: id, file: fileStore.getFile(id), metadata: draftFileMetadata?.[id] ?? {} }))
            .filter(item => item.file !== undefined),
      [draftFileIds, draftFileMetadata],
   );

   // ─── Return ─────────────────────────────────────────────────────────────────

   return {
      file,
      metadata,
      caption,
      isMediaPreview,
      mediaFeatures,
      isProcessingMedia,
      saveCaption,
      openMediaPreview,
      closeMediaPreview,
      closePostPreview,
      addMoreFiles,
      removeFile,
      setActiveFile,
      getFileById,
      getFilesWithMetadata,
   };
};
